/**
 * useShopCartDrawer — état d'ouverture du panneau latéral « panier ».
 *
 * L'état vit dans `useState` (partagé à l'échelle de l'app) et non dans un
 * composant : le panneau est monté au niveau de la PAGE (vitrine ou fiche
 * produit), mais il est ouvert depuis des endroits variés — le bouton d'ajout
 * de la fiche produit, l'icône panier de l'en-tête, la barre mobile. Faire
 * remonter un `v-model` depuis chacun aurait imposé une chaîne d'événements à
 * travers 3 niveaux de composants pour un simple booléen.
 *
 * `openedBy` mémorise l'élément déclencheur pour lui RENDRE le focus à la
 * fermeture (exigence d'accessibilité : sans ça, le focus repart en haut du
 * document et l'utilisateur au clavier perd sa place dans la grille produits).
 */
export function useShopCartDrawer() {
  const isOpen = useState<boolean>("shop-cart-drawer-open", () => false);
  /** Élément à re-focuser à la fermeture — jamais persisté, jamais sérialisé. */
  const openedBy = useState<HTMLElement | null>(
    "shop-cart-drawer-trigger",
    () => null,
  );

  /**
   * Ouvre le panneau.
   * @param trigger élément déclencheur (bouton) à re-focuser à la fermeture.
   */
  function open(trigger?: HTMLElement | null) {
    openedBy.value = trigger ?? null;
    isOpen.value = true;
  }

  function close() {
    isOpen.value = false;
    const trigger = openedBy.value;
    openedBy.value = null;
    if (!trigger || !import.meta.client) return;
    // `nextTick` : le panneau doit être démonté avant de rendre le focus,
    // sinon le piège à focus le récupère aussitôt.
    void nextTick(() => {
      if (trigger.isConnected) trigger.focus({ preventScroll: true });
    });
  }

  return { isOpen, open, close };
}
